import { useTranslation } from 'react-i18next';
import { AccordionDetails, Checkbox, FormControlLabel, Grid, TextField, Typography, useTheme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import React from 'react';
import moment from 'moment';
import { useFormikContext } from 'formik';
import { UsersMultiSelect } from '../../../../components/UsersMultiSelect';
import {
  AccountData,
  ActionSource,
  OrderData,
  OrderPageData,
  OrderStatus,
  PropelUserModel,
  VoucherType,
} from '../../../../../api';
import { Accordion, AccordionSummary, AccordionSummaryVariant } from '../Accordion';
import { EditOrderFormValues, EditOrderPanels } from '../../EditOrderDialog';
import { ManagementPanelSummary } from './ManagementPanelSummary';
import CopyToClipboardButton from '../../../CopyToClipboardButton';
import { useAuth } from '../../../../../session/InternalAuthProvider';
import { getEffectivePageLocationId } from '../../../../../common/filter/locationGrouping';
import { getDateFormat } from '../../../../../common/getDateAndTimeFormat';
import { PENDING_ORDER_STATUSES } from '../../../../../common/pendingOrderUtils';
import { UtmBlock } from '../../../../../common/ui/UtmBlock';
import { MANAGEMENT_COMMENT_FIELD_ID } from '../../../../../common/OrderUtils';
// @ts-ignore
import { useEditOrder } from '../../EditOrderContext';

type ManagementPanelProps = {
  isEventOrderPage: boolean;
  users: PropelUserModel[];
  expanded?: EditOrderPanels;
  handleChange: any;
  orderId?: string;
  isOneOfVoucherIntents: boolean;
  orderOrVoucher: Partial<OrderData>;
  orderPage?: OrderPageData;
};
export const ManagementPanel = ({
  isEventOrderPage,
  users,
  expanded,
  handleChange,
  orderId,
  isOneOfVoucherIntents,
  orderOrVoucher,
  orderPage,
}: ManagementPanelProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { values, setFieldValue } = useFormikContext<EditOrderFormValues>();
  // @ts-ignore
  const { editOrderState } = useEditOrder();
  const isMonetaryVoucher = editOrderState.voucherType === VoucherType.MONETARY;
  const monetaryValidityYears = account.monetary_voucher_settings?.validity_years;
  const defaultValidityYears = isMonetaryVoucher
    ? monetaryValidityYears
    : orderPage?.effective_vouchers_config?.max_valid_days &&
      Math.round((orderPage.effective_vouchers_config.max_valid_days / 365) * 10) / 10;
  const { selectedUsersIds, comment, shouldUnarchive, expirationDate } = values;

  return (
    <Accordion
      onChange={handleChange}
      expandedPanelName={expanded}
      panelName={EditOrderPanels.ManagementPanel}
      allOpenValue={EditOrderPanels.AllOpen}>
      <AccordionSummary
        variant={AccordionSummaryVariant.CAPTION}
        title={t('calendar.edit_order.magmntSection')}
        summaryComponent={
          <ManagementPanelSummary
            orderStatus={orderOrVoucher.status}
            shouldHideUsers={isOneOfVoucherIntents}
            comment={comment}
            selectedUsersIds={selectedUsersIds}
            users={users}
            isEventOrderPage={isEventOrderPage}
          />
        }
      />
      <AccordionDetails>
        <Grid container flexDirection="column" gap={2}>
          {!isEventOrderPage && !isOneOfVoucherIntents && (
            <UsersMultiSelect
              users={users}
              selectedUsersIds={selectedUsersIds}
              setSelectedUsersIds={(ids) => setFieldValue('selectedUsersIds', ids)}
              locationId={getEffectivePageLocationId(orderPage?.location_id, account)}
            />
          )}

          <TextField
            id={MANAGEMENT_COMMENT_FIELD_ID}
            multiline
            label={t('calendar.edit_event.comments')}
            type="text"
            value={comment}
            onChange={(e) => setFieldValue('comment', e.target.value)}
          />

          {isOneOfVoucherIntents && orderPage && (
            <Grid
              container
              gap={0.5}
              flexDirection="column"
              borderRadius="8px"
              border="1px solid"
              borderColor="divider"
              p={1}>
              <Typography fontWeight={700}>
                {t('vouchers.expiration_date')}
                {defaultValidityYears && ` (${t('vouchers.default_validity_years', { years: defaultValidityYears })})`}
              </Typography>
              <Grid container flexDirection="column">
                <Grid item container mb={2} mt={-0.5}>
                  <Typography variant="body2" fontWeight={500}>
                    {t('vouchers.createdat', {
                      date: moment(orderOrVoucher.created_at).format(getDateFormat(account).longDateFormat),
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    label={t('vouchers.select_expiration_date')}
                    format={getDateFormat(account).longDateFormat}
                    value={expirationDate ? moment(expirationDate) : null}
                    minDate={moment()}
                    maxDate={isMonetaryVoucher ? undefined : moment().add(5, 'years')}
                    onChange={(date) => setFieldValue('expirationDate', date?.format('YYYY-MM-DD') || '')}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}

          {orderId && (authState.account as unknown as AccountData).returning_customer_settings && (
            <Grid
              container
              gap={0.5}
              flexDirection="column"
              borderRadius="8px"
              border="1px solid"
              borderColor="divider"
              p={1}>
              <Typography fontWeight={700}>{`${t('calendar.order_popper.returningCsutomer')}`}</Typography>
              <Grid container gap={1} alignItems="center">
                <Typography>{orderId}</Typography>
                <CopyToClipboardButton width={'18px'} color={theme.palette.text.secondary} text={orderId} />
              </Grid>
            </Grid>
          )}
          {orderId && (
            <Grid
              container
              gap={0.5}
              flexDirection="column"
              borderRadius="8px"
              border="1px solid"
              borderColor="divider"
              p={1}>
              <Typography fontWeight={700}>{t('calendar.edit_order.status')}</Typography>
              <Typography> {t(`order_status.${orderOrVoucher?.status?.toLowerCase()}`)}</Typography>
              {orderOrVoucher.created_at && (
                <Typography variant="body2" color="text.secondary">
                  {t('calendar.edit_order.created_at')}:{' '}
                  {moment(orderOrVoucher.created_at).format(getDateFormat(account).longDateFormat)}
                </Typography>
              )}
              {(orderOrVoucher.source === ActionSource.API || orderOrVoucher.resolved_by_user_id) &&
                orderOrVoucher.status &&
                !PENDING_ORDER_STATUSES.includes(orderOrVoucher.status) &&
                (() => {
                  const resolvedByUser = users.find((user) => user.user_id === orderOrVoucher.resolved_by_user_id);
                  const resolvedByName = resolvedByUser
                    ? `${resolvedByUser.first_name} ${resolvedByUser.last_name}`
                    : orderOrVoucher.resolved_by_user_id;
                  const resolvedByLabel =
                    orderOrVoucher.source === ActionSource.API
                      ? `API${resolvedByName ? ` (${resolvedByName})` : ''}`
                      : resolvedByName;
                  return (
                    <Typography variant="body2" color="text.secondary">
                      {t('calendar.edit_order.resolvedBy')}: {resolvedByLabel}
                    </Typography>
                  );
                })()}
              {orderOrVoucher?.status === OrderStatus.ARCHIVED && (
                <>
                  {orderOrVoucher.archived_by_user_id && (
                    <Typography variant="body2" color="text.secondary">
                      {t('calendar.edit_order.archived_by')}:{' '}
                      {(() => {
                        const archivedByUser = users.find((u) => u.user_id === orderOrVoucher.archived_by_user_id);
                        return archivedByUser
                          ? `${archivedByUser.first_name} ${archivedByUser.last_name}`
                          : orderOrVoucher.archived_by_user_id;
                      })()}
                    </Typography>
                  )}
                  {orderOrVoucher.archive_timestamp && (
                    <Typography variant="body2" color="text.secondary">
                      {t('calendar.edit_order.archived_at')}:{' '}
                      {moment(orderOrVoucher.archive_timestamp).format(getDateFormat(account).longDateFormat)}
                    </Typography>
                  )}
                  <FormControlLabel
                    control={<Checkbox />}
                    checked={shouldUnarchive}
                    label={
                      <Grid container gap={1}>
                        <Typography>{t('calendar.edit_order.changeBackTo')}</Typography>
                        <Typography fontWeight={700}>
                          {t(`order_status.${orderOrVoucher?.last_status?.toLowerCase()}`)}
                        </Typography>
                      </Grid>
                    }
                    onChange={() => setFieldValue('shouldUnarchive', !shouldUnarchive)}
                  />
                </>
              )}
            </Grid>
          )}

          {orderOrVoucher && <UtmBlock utmData={orderOrVoucher.utm_data} />}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
